/*
 * Headless Algolia autocomplete.
 *
 * Replaces algolia_autocomplete.js. Instead of emptying the header search
 * container and letting autocomplete-js build a new search box, this binds
 * @algolia/autocomplete-core (assets/algolia-autocomplete-core.min.js) to the
 * existing markup in sections/header.liquid, so text typed before Algolia
 * loaded is never lost.
 *
 * The rendering below is a port of autocomplete-js 1.9.4 (autocomplete.js,
 * render.js, getPanelPlacementStyle.js) so the panel DOM, class names and
 * behaviour match what the Algolia app rendered before. The Algolia app's
 * plugins and templates (snippets/algolia_autocomplete_*.liquid) are reused
 * unchanged.
 *
 * Loaded by snippets/algolia-search-loader.liquid.
 */
(async function (window, document, algolia) {
  'use strict';

  if (!algolia || !algolia.config || !algolia.config.autocomplete_enabled) return;

  var loader = window.ThemeAlgoliaSearch;
  var autocompleteCore = window['@algolia/autocomplete-core'];
  var elements = loader && loader.getElements();
  if (!autocompleteCore || !elements || elements.root.__themeAutocomplete) return;
  elements.root.__themeAutocomplete = true;

  var config = algolia.config;
  var html = algolia.externals.html;
  var renderVNode = algolia.externals.render;

  var dom = {
    root: elements.root,
    form: elements.form,
    input: elements.input,
    label: elements.root.querySelector('.aa-Label'),
    loadingIndicator: elements.root.querySelector('.aa-LoadingIndicator'),
    clearButton: elements.clearButton,
    detachedButtonQuery: elements.detachedButtonQuery,
    detachedButtonPlaceholder: elements.detachedButtonPlaceholder,
    panel: document.createElement('div')
  };
  dom.panel.className = 'aa-Panel';

  // The plugin files assign themselves after awaiting the same promise.
  await config.index_suffix;
  await Promise.resolve();

  algolia.searchClient.addAlgoliaAgent('Shopify Integration');

  var plugins = [
    config.index_products && algolia.productsPlugin,
    config.index_collections && algolia.collectionsPlugin,
    config.index_articles && algolia.articlesPlugin,
    config.index_pages && algolia.pagesPlugin,
    config.autocomplete_query_suggestions && algolia.querySuggestionsPlugin
  ].filter(Boolean);

  // Same CSS setup as the Algolia app's algolia_autocomplete.js.
  var rootStyle = document.documentElement.style;
  rootStyle.setProperty('--aa-text-color-rgb', algolia.helpers.hexToRGB(config.colors.secondary));
  rootStyle.setProperty('--aa-primary-color-rgb', algolia.helpers.hexToRGB(config.colors.main));
  rootStyle.setProperty('--aa-muted-color-rgb', algolia.helpers.hexToRGB(config.colors.muted));
  rootStyle.setProperty('--aa-highlight-color-rgb', algolia.helpers.hexToRGB(config.colors.highlight));
  var cssTemplate = document.getElementById('template_algolia_autocomplete.css');
  if (cssTemplate) algolia.appendStyle(cssTemplate.text);

  /* ------------------------------------------------------------------
     DOM property helpers (port of autocomplete-js utils/setProperties)
     ------------------------------------------------------------------ */

  var TOUCH_EVENTS_ALIASES = ['ontouchstart', 'ontouchend', 'ontouchmove', 'ontouchcancel'];

  function eventProxy(event) {
    this._themeListeners[event.type](event);
  }

  function setProperty(node, name, value) {
    if (name === 'onChange') name = 'onInput';

    if (name === 'style') {
      for (var key in value) {
        node.style[key] = typeof value[key] === 'number' ? value[key] + 'px' : value[key];
      }
    } else if (name[0] === 'o' && name[1] === 'n') {
      var nameLower = name.toLowerCase();
      if (nameLower in node || TOUCH_EVENTS_ALIASES.indexOf(nameLower) !== -1) name = nameLower;
      var type = name.slice(2);
      if (!node._themeListeners) node._themeListeners = {};
      var hadListener = Boolean(node._themeListeners[type]);
      node._themeListeners[type] = value;
      if (value && !hadListener) {
        node.addEventListener(type, eventProxy);
      } else if (!value) {
        node.removeEventListener(type, eventProxy);
      }
    } else if (name !== 'list' && name !== 'tagName' && name !== 'form' && name !== 'type' && name !== 'size' && name in node) {
      node[name] = value == null ? '' : value;
    } else if (typeof value !== 'function') {
      if (value == null || (value === false && !/^ar/.test(name))) {
        node.removeAttribute(name);
      } else {
        node.setAttribute(name, value);
      }
    }
  }

  function setProperties(node, props, options) {
    if (!node) return;
    var withoutEvents = options && options.withoutEvents;
    var skip = (options && options.skip) || [];
    for (var name in props) {
      if (skip.indexOf(name) !== -1) continue;
      if (withoutEvents && name[0] === 'o' && name[1] === 'n') continue;
      setProperty(node, name, props[name]);
    }
  }

  // The header markup owns these: the placeholder is animated by
  // sections/search-placeholder.liquid, and the form action is the no-JS
  // fallback (autocomplete-core prevents the native submit once bound).
  var INPUT_SKIP = ['placeholder', 'autoFocus'];
  var FORM_SKIP = ['action'];

  /* ------------------------------------------------------------------
     Highlight components (port of autocomplete-js components)
     ------------------------------------------------------------------ */

  var HIGHLIGHT_PRE_TAG = '__aa-highlight__';
  var HIGHLIGHT_POST_TAG = '__/aa-highlight__';

  function getAttributeValueByPath(record, path) {
    return path.reduce(function (current, key) {
      return current && current[key];
    }, record);
  }

  function parseHighlightedValue(highlightedValue) {
    var parts = [];
    function add(part) {
      var last = parts[parts.length - 1];
      if (last && last.isHighlighted === part.isHighlighted) {
        last.value += part.value;
      } else {
        parts.push(part);
      }
    }
    var preTagParts = highlightedValue.split(HIGHLIGHT_PRE_TAG);
    var first = preTagParts.shift();
    if (first) add({ value: first, isHighlighted: false });
    preTagParts.forEach(function (part) {
      var postTagParts = part.split(HIGHLIGHT_POST_TAG);
      add({ value: postTagParts[0], isHighlighted: true });
      if (postTagParts[1] !== '') add({ value: postTagParts[1], isHighlighted: false });
    });
    return parts;
  }

  function createHighlightComponent(resultKey) {
    return function (options) {
      var path = Array.isArray(options.attribute) ? options.attribute : [options.attribute];
      var tagName = options.tagName || 'mark';
      var value = getAttributeValueByPath(options.hit, [resultKey].concat(path, ['value']));
      if (typeof value !== 'string') value = getAttributeValueByPath(options.hit, path) || '';
      return parseHighlightedValue(String(value)).map(function (part, index) {
        return part.isHighlighted ? html`<${tagName} key=${index}>${part.value}</${tagName}>` : part.value;
      });
    };
  }

  var components = {
    Highlight: createHighlightComponent('_highlightResult'),
    Snippet: createHighlightComponent('_snippetResult')
  };

  /* ------------------------------------------------------------------
     Autocomplete
     ------------------------------------------------------------------ */

  // autocomplete-js only falls back to the global renderNoResults when no
  // source supplies its own noResults template. This theme's product source
  // does (it lists trending products), and that must win.
  function hasNoResultsSourceTemplate(state) {
    return state.collections.some(function (collection) {
      return Boolean(collection.source.templates.noResults);
    });
  }

  function getItemsCount(state) {
    return state.collections.reduce(function (sum, collection) {
      return sum + collection.items.length;
    }, 0);
  }

  // The loader decides this. 'none' keeps the search box inline at every
  // width, matching the Algolia app file's detachedMediaQuery on this theme;
  // mobile search is the theme's own overlay in assets/custom.min.js.
  function detachedMediaQuery() {
    if (loader.detachedMediaQuery) return loader.detachedMediaQuery;
    var value = window.getComputedStyle(document.documentElement).getPropertyValue('--aa-detached-media-query');
    return value && value.trim() ? value.trim() : '(max-width: 680px)';
  }

  function matchesDetached() {
    var query = detachedMediaQuery();
    return query !== 'none' && window.matchMedia(query).matches;
  }

  var isDetached = matchesDetached();
  var renderTimer = null;

  var lastState = {
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: dom.input.value,
    activeItemId: null,
    status: 'idle'
  };

  var autocomplete = autocompleteCore.createAutocomplete({
    // Matches the ids already in the header markup.
    id: 'autocomplete-0',
    openOnFocus: false,
    debug: config.autocomplete_debug,
    insights: config.analytics_enabled,
    plugins: plugins,
    initialState: { query: dom.input.value },
    shouldPanelOpen: function (params) {
      if (isDetached) return true;
      var hasItems = getItemsCount(params.state) > 0;
      if (!params.state.query) return hasItems || hasNoResultsSourceTemplate(params.state);
      // A no-results template exists, so a query always opens the panel.
      return true;
    },
    onStateChange: function (params) {
      handleStateChange(params.state, params.prevState);
    },
    onSubmit: function (params) {
      window.location.href = window.Shopify.routes.root + 'search?q=' + encodeURIComponent(params.state.query);
    }
  });

  var scopeApi = {
    setActiveItemId: autocomplete.setActiveItemId,
    setQuery: autocomplete.setQuery,
    setCollections: autocomplete.setCollections,
    setIsOpen: autocomplete.setIsOpen,
    setStatus: autocomplete.setStatus,
    setContext: autocomplete.setContext,
    refresh: autocomplete.refresh,
    navigator: autocomplete.navigator
  };

  /* Bind the existing markup ------------------------------------------ */

  setProperties(window, autocomplete.getEnvironmentProps({
    formElement: dom.form,
    panelElement: dom.panel,
    inputElement: dom.input
  }));

  setProperties(dom.root, autocomplete.getRootProps({}));
  setProperties(dom.label, autocomplete.getLabelProps({}));
  setProperties(dom.form, autocomplete.getFormProps({ inputElement: dom.input }), { skip: FORM_SKIP });
  setProperties(dom.panel, autocomplete.getPanelProps({}));

  var inputProps = autocomplete.getInputProps({ inputElement: dom.input });
  var coreOnKeyDown = inputProps.onKeyDown;
  inputProps.onKeyDown = function (event) {
    // In detached mode we don't want to close the panel when hitting Tab.
    if (isDetached && event.key === 'Tab') return;
    coreOnKeyDown(event);
  };
  setProperties(dom.input, inputProps, { skip: INPUT_SKIP });

  /* Rendering ------------------------------------------------------------ */

  function handleStateChange(state, prevState) {
    lastState = state;

    if (isDetached && prevState.isOpen && !state.isOpen && loader.closeDetached) {
      loader.closeDetached();
    }

    // The header may have moved since the panel was last positioned.
    if (!isDetached && state.isOpen && !prevState.isOpen) {
      setPanelPosition();
    }

    if (state.query !== prevState.query) {
      document.querySelectorAll('.aa-Panel--scrollable').forEach(function (panel) {
        if (panel.scrollTop !== 0) panel.scrollTop = 0;
      });
    }

    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(function () {
      scheduleRender(lastState);
    }, 0);
  }

  function scheduleRender(state) {
    renderSearchBox(state);
    renderPanel(state);
  }

  function renderSearchBox(state) {
    setProperties(dom.root, autocomplete.getRootProps({}), { withoutEvents: true });
    setProperties(dom.input, autocomplete.getInputProps({ inputElement: dom.input }), { withoutEvents: true, skip: INPUT_SKIP });
    setProperties(dom.label, { hidden: state.status === 'stalled' });
    setProperties(dom.loadingIndicator, { hidden: state.status !== 'stalled' });
    setProperties(dom.clearButton, { hidden: !state.query });
    setProperties(dom.detachedButtonQuery, { textContent: state.query });
    setProperties(dom.detachedButtonPlaceholder, { hidden: Boolean(state.query) });
  }

  function renderPanel(state) {
    var panelContainer = isDetached && loader.getDetachedContainer ? loader.getDetachedContainer() : document.body;

    if (!state.isOpen) {
      if (dom.panel.parentNode) dom.panel.parentNode.removeChild(dom.panel);
      return;
    }

    // Add the panel once results have been fetched.
    if (dom.panel.parentNode !== panelContainer && state.status !== 'loading') {
      panelContainer.appendChild(dom.panel);
    }

    dom.panel.classList.toggle('aa-Panel--stalled', state.status === 'stalled');

    if (getItemsCount(state) === 0 && !hasNoResultsSourceTemplate(state)) {
      renderVNode(algolia.noResultsTemplate({ html: html, state: state }), dom.panel);
      return;
    }

    var sectionElements = {};

    state.collections.forEach(function (collection, index) {
      var source = collection.source;
      var items = collection.items;
      if (!source.templates.noResults && items.length === 0) return;

      var templateParams = { components: components, items: items, source: source, state: state, html: html };
      var header = source.templates.header && html`<div class="aa-SourceHeader">${source.templates.header(templateParams)}</div>`;
      var footer = source.templates.footer && html`<div class="aa-SourceFooter">${source.templates.footer(templateParams)}</div>`;
      var body;

      if (source.templates.noResults && items.length === 0) {
        body = html`<div class="aa-SourceNoResults">${source.templates.noResults(templateParams)}</div>`;
      } else {
        var listProps = autocomplete.getListProps({ sourceIndex: index });
        body = html`<ul class="aa-List" ...${listProps}>
          ${items.map(function (item) {
            var itemProps = autocomplete.getItemProps({ item: item, source: source, sourceIndex: index });
            return html`<li key=${itemProps.id} class="aa-Item" ...${itemProps}>
              ${source.templates.item({ components: components, item: item, state: state, html: html })}
            </li>`;
          })}
        </ul>`;
      }

      sectionElements[source.sourceId] = html`<section key=${index} class="aa-Source" data-autocomplete-source-id=${source.sourceId}>
        ${header}${body}${footer}
      </section>`;
    });

    // Same layout choice as the Algolia app's algolia_autocomplete.js.
    var hasLeftPanelResults = state.collections
      .filter(function (collection) {
        return collection.source.sourceId !== 'products';
      })
      .reduce(function (sum, collection) {
        return sum + collection.items.length;
      }, 0) > 0;

    var suggestions = state.collections.filter(function (collection) {
      return collection.source.sourceId === 'querySuggestionsPlugin';
    })[0];
    var displaySuggestions = Boolean(config.autocomplete_query_suggestions && suggestions && suggestions.items.length > 0);

    renderVNode(
      hasLeftPanelResults
        ? algolia.mainTemplate({ html: html, state: state }, sectionElements, displaySuggestions)
        : algolia.mainProductsTemplate({ html: html, state: state }, sectionElements),
      dom.panel
    );

    if (typeof window.updateAllProductCountdowns === 'function') {
      window.requestAnimationFrame(function () {
        window.updateAllProductCountdowns(dom.panel);
      });
    }
  }

  // Port of getPanelPlacementStyle with the default 'input-wrapper-width'.
  function setPanelPosition() {
    if (isDetached) {
      setProperties(dom.panel, { style: { top: '', left: '', right: '', width: '', maxWidth: '' } });
      return;
    }
    var containerRect = dom.root.getBoundingClientRect();
    var formRect = dom.form.getBoundingClientRect();
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    setProperties(dom.panel, {
      style: {
        top: scrollTop + containerRect.top + containerRect.height,
        left: formRect.left,
        right: document.documentElement.clientWidth - (formRect.left + formRect.width),
        width: 'unset',
        maxWidth: 'unset'
      }
    });
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      var wasDetached = isDetached;
      isDetached = matchesDetached();
      if (wasDetached !== isDetached) {
        autocomplete.setIsOpen(false);
        if (loader.closeDetached) loader.closeDetached();
        if (dom.panel.parentNode) dom.panel.parentNode.removeChild(dom.panel);
      } else {
        window.requestAnimationFrame(setPanelPosition);
      }
    }, 20);
  });

  loader.onDetachedClose = function () {
    if (lastState.isOpen) autocomplete.setIsOpen(false);
  };

  /* Hand over from the native input -------------------------------------- */

  loader.ready = true;
  algolia.autocompleteInstance = autocomplete;
  // assets/algolia_autocomplete_product_plugin.js calls this once its
  // trending products arrive, so the panel re-renders with them.
  algolia.refreshAutocomplete = function () {
    autocomplete.refresh();
  };
  scheduleRender(lastState);
  window.requestAnimationFrame(setPanelPosition);

  // If the visitor is already in the search box, behave as if they had just
  // focused it: this runs their query and opens the panel.
  if (document.activeElement === dom.input) {
    inputProps.onFocus(new window.Event('focus'));
  }
})(window, document, window.algoliaShopify);
